"use client";

import { useState, useEffect } from "react";
import Icon from "@/components/ui/AppIcon";

interface FormData {
  name: string;
  phone: string;
  email: string;
  projectType: string;
  budgetRange: string;
  message: string;
}

const ContactFormSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    email: "",
    projectType: "",
    budgetRange: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      // Submit directly to Google Forms
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.name = 'hidden_iframe';
      document.body.appendChild(iframe);

      const form = document.createElement('form');
      form.target = 'hidden_iframe';
      form.method = 'POST';
      form.action = 'https://docs.google.com/forms/d/e/1FAIpQLScHsiDAvG1HZ4EemC-mwtJPhRWHLDCs5ACwgha2v7tKi3G2Tw/formResponse';

      // Add form fields (entry IDs need to be updated with actual Google Form entry IDs)
      const fields = [
        { name: 'entry.1234567890', value: formData.name },
        { name: 'entry.0987654321', value: formData.phone },
        { name: 'entry.1122334455', value: formData.email },
        { name: 'entry.5544332211', value: formData.projectType },
        { name: 'entry.6677889900', value: formData.budgetRange },
        { name: 'entry.9988776655', value: formData.message },
      ];

      fields.forEach(field => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = field.name;
        input.value = field.value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(form);
        document.body.removeChild(iframe);
      }, 1000);

      setIsSubmitting(false);
      setSubmitMessage({
        type: "success",
        text: "Thank you! We'll contact you within 2 hours during business days.",
      });

      // Reset form
      setFormData({
        name: "",
        phone: "",
        email: "",
        projectType: "",
        budgetRange: "",
        message: "",
      });
    } catch (error: any) {
      console.error('Form submission error:', error);
      setIsSubmitting(false);
      setSubmitMessage({
        type: "error",
        text: "Failed to submit inquiry. Please try again or call us directly.",
      });
    }
  };

  const projectTypes = [
    { id: "type_construction", value: "House Construction", label: "House Construction" },
    { id: "type_land", value: "Land Purchase", label: "Land Purchase" },
    { id: "type_materials", value: "Building Materials", label: "Building Materials" },
    { id: "type_endtoend", value: "End-to-End Service", label: "End-to-End Service" },
    { id: "type_other", value: "Other", label: "Other" },
  ];

  const budgetRanges = [
    { id: "budget_20_30", value: "₹20-30 Lakhs", label: "₹20-30 Lakhs" },
    { id: "budget_30_40", value: "₹30-40 Lakhs", label: "₹30-40 Lakhs" },
    { id: "budget_40_50", value: "₹40-50 Lakhs", label: "₹40-50 Lakhs" },
    { id: "budget_50plus", value: "₹50+ Lakhs", label: "₹50+ Lakhs" },
    { id: "budget_discuss", value: "Prefer to Discuss", label: "Prefer to Discuss" },
  ];

  if (!isHydrated) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="h-96 bg-muted rounded-3xl animate-pulse"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className="p-8 md:p-12 bg-muted rounded-3xl border border-border">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-8">
              Send Us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="+91 98765 43210"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-medium text-foreground mb-2">
                  Project Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select project type</option>
                  {projectTypes.map((type) => (
                    <option key={type.id} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="budgetRange" className="block text-sm font-medium text-foreground mb-2">
                  Budget Range
                </label>
                <select
                  id="budgetRange"
                  name="budgetRange"
                  value={formData.budgetRange}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select budget range</option>
                  {budgetRanges.map((budget) => (
                    <option key={budget.id} value={budget.value}>
                      {budget.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  placeholder="Tell us about your project requirements..."
                ></textarea>
              </div>

              {submitMessage && (
                <div
                  className={`p-4 rounded-lg ${
                    submitMessage.type === "success" ?"bg-success/10 text-success border border-success/20" :"bg-error/10 text-error border border-error/20"
                  }`}
                >
                  <p className="text-sm font-medium">{submitMessage.text}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold hover:bg-accent transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={20} />
                    Send Message
                  </>
                )}
              </button>

              <p className="text-xs text-muted-foreground text-center">
                We respond within 2 hours during business days (Mon-Sat, 9 AM - 7 PM)
              </p>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-6">
                Contact Information
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Reach out to us directly via phone, WhatsApp, or visit our office. 
                We're here to answer all your questions about construction, land, and materials.
              </p>
            </div>

            <div className="space-y-6">
              {/* Phone */}
              <div className="p-6 bg-muted rounded-2xl border border-border hover:border-primary transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0 group-hover:bg-primary transition-all">
                    <Icon name="PhoneIcon" size={24} className="text-primary group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-foreground mb-2">Phone</h3>
                    <a
                      href="tel:+917890369000"
                      className="text-muted-foreground hover:text-primary transition-colors block"
                    >
                      +91 7890369000
                    </a>
                    <p className="text-sm text-muted-foreground mt-1">Mon-Sat: 9 AM - 7 PM</p>
                  </div>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="p-6 bg-muted rounded-2xl border border-border hover:border-secondary transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Icon name="ChatBubbleLeftRightIcon" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-foreground mb-2">WhatsApp</h3>
                    <a
                      href="https://wa.link/yurmqd"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary transition-colors block"
                    >
                      +91 7890369000
                    </a>
                    <p className="text-sm text-muted-foreground mt-1">Quick response guaranteed</p>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="p-6 bg-muted rounded-2xl border border-border hover:border-primary transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0 group-hover:bg-primary transition-all">
                    <Icon name="EnvelopeIcon" size={24} className="text-primary group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-foreground mb-2">Email</h3>
                    <a
                      href="mailto:sales@artienterprises.in"
                      className="text-muted-foreground hover:text-primary transition-colors block"
                    >
                      sales@artienterprises.in
                    </a>
                    <p className="text-sm text-muted-foreground mt-1">We'll reply within 24 hours</p>
                  </div>
                </div>
              </div>

              {/* Office Address */}
              <div className="p-6 bg-muted rounded-2xl border border-border">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                    <Icon name="MapPinIcon" size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-foreground mb-2">Office Address</h3>
                    <address className="text-muted-foreground not-italic leading-relaxed">
                      10/19/4, Yani Sarani<br />
                      Joka, Kolkata – 700104<br />
                      West Bengal, India
                    </address>
                    <p className="text-sm text-primary font-bold mt-2">Visit by appointment</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Contact CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <a
                href="tel:+917890369000"
                className="flex-1 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-center hover:bg-accent transition-all hover:scale-105 flex items-center justify-center gap-2"
              >
                <Icon name="PhoneIcon" size={20} />
                Call Now
              </a>
              <a
                href="https://wa.link/yurmqd"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-8 py-4 bg-secondary text-secondary-foreground rounded-lg font-bold text-center hover:bg-yellow-500 transition-all hover:scale-105 flex items-center justify-center gap-2"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={20} />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;